<template>
  <div class="team-dir">
    <div class="page-header">
      <div>
        <h1 class="page-title">{{ t('teamPage.title') }}</h1>
        <p class="page-sub">{{ t('teamPage.sub') }}</p>
      </div>
      <button v-if="isSuperadmin" class="btn btn-primary" @click="openForm">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
        {{ t('teamPage.addMember') }}
      </button>
    </div>

    <!-- Add member (superadmin only) -->
    <div v-if="showForm" class="modal-backdrop" @click.self="showForm = false">
      <div class="modal">
        <div class="modal-header">
          <h2>{{ t('teamPage.addMember') }}</h2>
          <button class="modal-close" @click="showForm = false">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <form @submit.prevent="submitForm">
          <div class="form-group">
            <label>{{ t('teamPage.memberName') }} *</label>
            <input v-model="form.name" class="form-control" required maxlength="120" />
          </div>
          <div class="form-group">
            <label>{{ t('teamPage.memberEmail') }} *</label>
            <input v-model="form.email" type="email" class="form-control" required maxlength="160" />
          </div>
          <div class="form-group">
            <label>{{ t('teamPage.memberPassword') }} *</label>
            <input v-model="form.password" type="password" class="form-control" required minlength="8" :placeholder="t('teamPage.passwordHint')" />
          </div>
          <div class="form-group">
            <label>{{ t('teamPage.memberRole') }}</label>
            <select v-model="form.role" class="form-control">
              <option value="developer">{{ t('roles.developer') }}</option>
              <option value="manager">{{ t('roles.manager') }}</option>
            </select>
          </div>
          <p v-if="formError" class="form-error">{{ formError }}</p>
          <div class="modal-actions">
            <button type="button" class="btn btn-ghost" @click="showForm = false">{{ t('common.cancel') }}</button>
            <button type="submit" class="btn btn-primary" :disabled="saving">
              {{ saving ? t('common.saving') : t('teamPage.addMember') }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <div v-if="loading" class="spinner"></div>

    <template v-else>
      <!-- Developers -->
      <div class="seclabel">{{ t('teamPage.developers') }}</div>
      <div class="dev-grid">
        <div v-for="u in users" :key="u.id" class="dev-card">
          <div class="dev-avatar">{{ initials(u.name) }}</div>
          <div class="dev-info">
            <div class="dev-name">{{ u.name }}</div>
            <div class="dev-role">{{ u.role === 'manager' ? t('roles.manager') : t('roles.developer') }}</div>
          </div>
        </div>
      </div>

      <!-- Shared projects -->
      <div class="seclabel">{{ t('teamPage.projects') }}</div>
      <div v-if="sharedProjects.length === 0" class="empty-state">
        <p>{{ t('teamPage.noProjects') }}</p>
      </div>
      <div v-else class="proj-grid">
        <router-link
          v-for="p in sharedProjects"
          :key="p.id"
          :to="'/tasks?project_id=' + p.id"
          class="proj-card"
        >
          <span class="proj-dot" :style="{ background: p.color }"></span>
          <div class="proj-body">
            <div class="proj-name">{{ p.name }}</div>
            <div class="proj-meta">{{ p.task_count }} {{ t('teamPage.tasksCount') }}</div>
          </div>
        </router-link>
      </div>
    </template>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import api from '@/api'
import { useI18n } from '@/composables/useI18n'
import { useAuth } from '@/composables/useAuth'
import { useToast } from '@/composables/useToast'

export default {
  name: 'TeamDirectoryView',
  setup() {
    const { t }  = useI18n()
    const { isSuperadmin: _isSuperadmin } = useAuth()
    const toast    = useToast()
    const isSuperadmin = computed(() => _isSuperadmin())
    const users    = ref([])
    const projects = ref([])
    const loading  = ref(true)

    const sharedProjects = computed(() => projects.value.filter(p => p.visibility !== 'private'))
    const initials = (name) => (name || '').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()

    async function loadUsers() {
      try { users.value = (await api.getUsers()).data || [] } catch {}
    }

    // ── Add member (superadmin only) ──
    const showForm  = ref(false)
    const saving    = ref(false)
    const formError = ref('')
    const form = reactive({ name: '', email: '', password: '', role: 'developer' })

    function openForm() {
      Object.assign(form, { name: '', email: '', password: '', role: 'developer' })
      formError.value = ''
      showForm.value  = true
    }
    async function submitForm() {
      formError.value = ''
      saving.value = true
      try {
        await api.createTeamMember({ ...form })
        showForm.value = false
        await loadUsers()
        toast.success(t('teamPage.memberAdded'))
      } catch (e) {
        const errs = e.data && e.data.errors
        formError.value = errs ? Object.values(errs).flat().join(' ') : (e.message || t('toast.saveFailed'))
      } finally {
        saving.value = false
      }
    }

    onMounted(async () => {
      try {
        const [u, p] = await Promise.all([api.getUsers(), api.getProjects()])
        users.value    = u.data || []
        projects.value = p.data || []
      } catch {} finally {
        loading.value = false
      }
    })

    return {
      t, isSuperadmin, users, sharedProjects, initials, loading,
      showForm, saving, formError, form, openForm, submitForm,
    }
  },
}
</script>

<style scoped>
.page-sub { color: var(--muted); font-size: 13px; margin-top: 2px; }
.page-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; }

/* Add-member modal */
.modal-backdrop {
  position: fixed; inset: 0;
  background: rgba(0,0,0,.6);
  display: flex; align-items: center; justify-content: center;
  z-index: 1000; padding: 20px;
}
.modal {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 12px;
  width: 100%; max-width: 440px;
  padding: 28px;
  box-shadow: var(--shadow);
}
.modal-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px; }
.modal-header h2 { font-size: 18px; font-weight: 700; }
.modal-close { border: none; background: none; cursor: pointer; color: var(--muted); display: flex; }
.form-group { margin-bottom: 14px; }
.form-group label { display: block; font-size: 13px; color: var(--muted); margin-bottom: 6px; }
.form-error {
  color: var(--danger); font-size: 13px; margin-bottom: 12px;
  padding: 8px 12px; background: rgba(239,68,68,.1); border-radius: 6px;
}
.modal-actions {
  display: flex; justify-content: flex-end; gap: 10px;
  margin-top: 20px; padding-top: 16px; border-top: 1px solid var(--border);
}
.seclabel {
  font-family: var(--font-mono);
  font-size: 11px; letter-spacing: .12em; text-transform: uppercase;
  color: var(--faint);
  margin: 28px 0 14px;
  display: flex; align-items: center; gap: 12px;
}
.seclabel::after { content: ""; flex: 1; height: 1px; background: var(--border); }

.dev-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 12px;
}
.dev-card {
  display: flex; align-items: center; gap: 12px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 14px 16px;
}
.dev-avatar {
  width: 38px; height: 38px; border-radius: 8px;
  background: var(--accent); color: var(--on-accent);
  display: grid; place-items: center; font-weight: 600; font-size: 13px;
  flex-shrink: 0;
}
.dev-name { font-size: 14px; font-weight: 600; }
.dev-role { font-size: 12px; color: var(--muted); margin-top: 1px; }

.proj-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 12px;
}
.proj-card {
  display: flex; align-items: center; gap: 12px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 14px 16px;
  text-decoration: none; color: var(--text);
  transition: border-color .12s, background .12s;
}
.proj-card:hover { border-color: var(--accent); background: var(--accent-wash); }
.proj-dot { width: 12px; height: 12px; border-radius: 4px; flex-shrink: 0; }
.proj-name { font-size: 14px; font-weight: 600; }
.proj-meta { font-size: 12px; color: var(--muted); margin-top: 1px; }
</style>
